import React from "react"
import {useTranslation} from "react-i18next"
import {UserIcon} from "@heroicons/react/24/outline"
import {twMerge} from "tailwind-merge"

import {InputBase} from "../../../components/fields/Input.tsx"
import {DropdownContext} from "../../../components/formElements/Dropdown/context.ts"
import {DefaultSelectedValue} from "../../../components/formElements/Dropdown/defaultComponents/DefaultSelectedValue.tsx"
import {DropdownBase} from "../../../components/formElements/Dropdown/Dropdown.tsx"
import {TOption} from "../../../components/formElements/Dropdown/types.ts"
import {useSalesAssistantSalesPeopleSelectBoxQuery} from "../../../queries/salesAssistant.ts"
import {AKlaraDashboardDealsMeta, ASalesAssistantDealPriorities} from "../../../services/types.generated.ts"
import {enumToOptions, enumTranslKey} from "../../../utils/i18n.tsx"
import {EMPTY_ARRAY, getFullName} from "../../../utils/index.ts"
import {KlaraFiltersContext} from "../filters.tsx"
import {useKlaraSalesPeople} from "../useKlaraSalesPeople.ts"
import {CARD_TITLE_CLASS_NAME} from "./cardStyles.ts"
import {PRIORITY_DOT_CLASS_NAME} from "./PriorityMenu.tsx"

const SEARCH_DEBOUNCE_MS = 500

const priorityOptions = enumToOptions("SalesAssistantDealPriority") as Array<TOption<ASalesAssistantDealPriorities>>

type TFacetCounts = Pick<AKlaraDashboardDealsMeta["counts"], "sales_people" | "priorities">

/**
 * The leads heading and filters. `total` may belong to a previous filter or a refreshing response (`isTotalStale`,
 * shown dimmed); option `counts` are only passed while they match the current filters.
 */
export const LeadsToolbar: React.FC<{
  titleId: string
  total: number | undefined
  isTotalStale: boolean
  counts: TFacetCounts | undefined
}> = ({titleId, total, isTotalStale, counts}) => {
  const {t} = useTranslation()
  const {
    salesPersonExternalId,
    setSalesPersonExternalId,
    priorities,
    setPriorities,
    search,
    setSearch,
    searchResetVersion,
  } = KlaraFiltersContext.useContext()

  // The options are the period's people; the company list only names a selected person who is not among them.
  const periodPeople = useKlaraSalesPeople()
  const companySalesPeople = useSalesAssistantSalesPeopleSelectBoxQuery()
  const people = periodPeople.data?.data.sales_people ?? EMPTY_ARRAY
  // Only a settled response for this period, never a pending, failed or previous period's one, proves no activity.
  const isPeriodConfirmed = periodPeople.isSuccess && !periodPeople.isPlaceholderData
  const salesPeopleCounts = counts?.sales_people
  const salesPeopleOptions = React.useMemo<Array<TOption<string>>>(() => {
    // A person the leads match nowhere has no entry in the counts.
    const withCount = (title: string, externalId: string) =>
      salesPeopleCounts
        ? t("KlaraDashboard_Leads_OptionCount", {title, count: salesPeopleCounts[externalId] ?? 0})
        : title
    const options = people.map(({sales_person: salesPerson}) => ({
      value: salesPerson.external_id,
      title: withCount(getFullName(salesPerson), salesPerson.external_id),
    }))
    if (!salesPersonExternalId || options.some(option => option.value === salesPersonExternalId)) {
      return options
    }

    const selected = companySalesPeople.data?.data.sales_people?.find(
      salesPerson => salesPerson.external_id === salesPersonExternalId
    )
    const name = getFullName(
      selected,
      companySalesPeople.isPending
        ? t("KlaraDashboard_PersonChip_Pending")
        : t("KlaraDashboard_UpdateCenter_UnknownSalesPerson")
    )
    return [
      ...options,
      {
        value: salesPersonExternalId,
        title: withCount(
          isPeriodConfirmed ? t("KlaraDashboard_Leads_SalesPersonNoActivity", {name}) : name,
          salesPersonExternalId
        ),
      },
    ]
  }, [
    people,
    isPeriodConfirmed,
    companySalesPeople.data,
    companySalesPeople.isPending,
    salesPersonExternalId,
    salesPeopleCounts,
    t,
  ])

  const priorityCounts = counts?.priorities
  const priorityOptionsWithCounts = React.useMemo<Array<TOption<ASalesAssistantDealPriorities>>>(
    () =>
      priorityCounts
        ? priorityOptions.map(option => ({
            ...option,
            title: t("KlaraDashboard_Leads_OptionCount", {
              title: t(enumTranslKey("SalesAssistantDealPriority", option.value)),
              count: priorityCounts[option.value],
            }),
          }))
        : priorityOptions,
    [priorityCounts, t]
  )

  return (
    <div className={"flex flex-wrap items-center gap-3"}>
      <h2 id={titleId} className={twMerge(CARD_TITLE_CLASS_NAME, "flex items-baseline gap-2.5")}>
        {t("KlaraDashboard_Leads_Title")}
        {total != null && (
          <span
            aria-busy={isTotalStale}
            className={twMerge(
              "text-xs font-normal tracking-normal whitespace-nowrap text-cr-slate-60 transition-opacity",
              isTotalStale && "opacity-50"
            )}
          >
            <span className={"sr-only"}> · </span>
            {t("KlaraDashboard_Leads_Count", {count: total})}
          </span>
        )}
      </h2>
      <div className={"ml-auto flex flex-wrap items-center gap-2"}>
        {/* Sized by their content: a selected name or several priorities widen them, up to a cap, then truncate. */}
        <div className={"max-w-60 min-w-[148px]"}>
          <DropdownBase
            name={"klaraLeadsSalesPerson"}
            options={salesPeopleOptions}
            value={salesPersonExternalId}
            onChange={setSalesPersonExternalId}
            clearable
            ariaLabel={t("KlaraDashboard_Leads_SalesPersonFilter")}
            clearButtonAriaLabel={t("KlaraDashboard_Leads_ClearSalesPersonFilter")}
            placeholder={t("KlaraDashboard_Leads_Column_SalesPerson")}
            listWidth={"list-auto"}
            mainButtonClassName={filterButtonClassName(salesPersonExternalId !== undefined)}
            caretClassName={FILTER_CARET_CLASS_NAME}
            SelectedValue={SalesPersonSelectedValue}
          />
        </div>
        <div className={"max-w-52 min-w-[108px]"}>
          <DropdownBase
            name={"klaraLeadsPriority"}
            multiple
            options={priorityOptionsWithCounts}
            value={priorities}
            onChange={value => setPriorities(value ?? [])}
            clearable
            ariaLabel={t("KlaraDashboard_Leads_PriorityFilter")}
            clearButtonAriaLabel={t("KlaraDashboard_Leads_ClearPriorityFilter")}
            placeholder={t("KlaraDashboard_Leads_Column_Priority")}
            listWidth={"list-auto"}
            mainButtonClassName={filterButtonClassName(priorities.length > 0)}
            caretClassName={FILTER_CARET_CLASS_NAME}
            SelectedValue={PrioritySelectedValue}
          />
        </div>
        {/* Clearing the filters remounts the input, dropping a draft (and its timer) that was not committed yet. */}
        <LeadsSearch key={searchResetVersion} search={search} setSearch={setSearch} />
      </div>
    </div>
  )
}

const filterButtonClassName = (isActive: boolean) =>
  twMerge(
    "gap-1 rounded-lg px-[11px] py-[5px] text-xs font-semibold ring-cr-grey-15 hover:ring-cr-blue-mid",
    isActive && "bg-cr-blue-super-light ring-cr-blue-mid"
  )

const FILTER_CARET_CLASS_NAME = "text-cr-slate-60 [&_svg]:size-4"

const SalesPersonSelectedValue: React.FC = () => (
  <span className={"flex min-w-0 items-center gap-2 [&>div]:text-cr-slate-90"}>
    <UserIcon className={"size-3.5 shrink-0 text-cr-slate-60"} aria-hidden={"true"} />
    <DefaultSelectedValue />
  </span>
)

/** The selected priority's dot, or a neutral one for none or several. */
const PrioritySelectedValue: React.FC = () => {
  const context = DropdownContext.useContext()
  const selected = context.multiple ? context.value : null
  const single = selected?.length === 1 ? (selected[0].value as ASalesAssistantDealPriorities) : null

  return (
    <span className={"flex min-w-0 items-center gap-2 [&>div]:text-cr-slate-90"}>
      <span
        aria-hidden={"true"}
        className={twMerge(
          "size-[7px] shrink-0 rounded-full bg-cr-slate-30",
          single && PRIORITY_DOT_CLASS_NAME[single]
        )}
      />
      <DefaultSelectedValue />
    </span>
  )
}

/** Keeps the typed draft locally and commits it after a pause, so a request never pairs a new search with an old page. */
const LeadsSearch: React.FC<{search: string; setSearch: (search: string) => void}> = ({search, setSearch}) => {
  const {t} = useTranslation()
  const [draft, setDraft] = React.useState(search)

  // A search cleared or replaced from outside (e.g. "Clear filters") replaces the draft.
  const [seenSearch, setSeenSearch] = React.useState(search)
  if (search !== seenSearch) {
    setSeenSearch(search)
    setDraft(search)
  }

  React.useEffect(() => {
    if (draft === search) {
      return
    }

    const timeout = window.setTimeout(() => setSearch(draft), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timeout)
  }, [draft, search, setSearch])

  return (
    <InputBase
      type={"search"}
      Icon={false}
      // The shared `.input-border > input` text size outranks plain utilities on the input, hence the overrides.
      borderClassName={
        "w-[220px] rounded-lg ring-cr-grey-15 [&>input]:px-2.5! [&>input]:py-[7px]! [&>input]:text-xs! [&>input]:leading-[18px]!"
      }
      inputClassName={"placeholder:text-cr-slate-40"}
      aria-label={t("KlaraDashboard_Leads_Search")}
      placeholder={t("KlaraDashboard_Leads_Search")}
      value={draft}
      onChange={event => setDraft(event.target.value)}
    />
  )
}
